import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DURATION, useSnackbar } from 'baseui/snackbar';
import { useAtomValue, useSetAtom } from 'jotai';
import _isNaN from 'lodash/isNaN';
import _merge from 'lodash/merge';
import _noop from 'lodash/noop';
import { createContext } from 'use-context-selector';
import { useAPIProvider } from '../../../common/hooks/use-api-provider';
import { useFrameDecoder } from '../../../common/hooks/use-frame-decoder';
import { useFrameDetailsExtractor } from '../../../common/hooks/use-frame-details-extractor';
import useTaskStateStore from '../../../common/task-state/providers/hooks/useTaskStateStore';
import { FRAME_DETAILS_STATUS } from '../../../workers/constants/frame-processor-constants';
import { getClientErrorMessage } from '../../../workers/utils/video-error-messages';
import { annotationFramesAtom, annotationLabelsAtom, frameCountAtom } from '../../labelling-v2/atoms/annotation-atoms';
import { fileTypeAtom } from '../../labelling-v2/atoms/file-atoms';
import { dispatchAtom } from '../../labelling-v2/atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../../labelling-v2/atoms/label-store-dispatch/events';
import { selectedVideoIndexAtom } from '../../labelling-v2/atoms/multi-video-atoms';
import { isLoadingAtom } from '../../labelling-v2/atoms/tool-atoms';
import { accurateFrameCountAtom, currentFrameAtom, frameDetailsStateAtom, frameTableAtom } from '../../labelling-v2/atoms/video-playback-atoms';
import { PERFORMANCE_METRIC_EVENTS } from '../../labelling-v2/constants/performance';
import { useMeasureLatency } from '../../labelling-v2/hooks/performance/use-measure-latency';
import { usePerformanceMetrics } from '../../labelling-v2/hooks/performance/use-performance-metrics';
import { usePlaybackQualityMetrics } from '../../labelling-v2/hooks/performance/use-playback-quality-metrics';
import { FPS } from '../components/frames-control-panel/constants';
import { MEDIA_EVENT_LISTENER } from '../components/video/constants/video-events';
import { createAnnotationLabelMap, getInitialVideoFrames } from '../utils';
import { findClosestFrameByTimestamp, getDefaultPlaySpeed, getInitialFrameLoadStatus } from './utils/mediaState';
import { DEFAULT_FRAME_RATE } from './constants';
export type MediaStateContextT = {
  isPlaying: boolean;
  mediaDuration: number;
  playSpeed: number;
  mediaFrameLoadStatus: boolean[];
  onPlaySpeedChange: (speed: number) => void;
  onPlayStatusChange: (state: boolean) => void;
  updateFrameLoadStatus: (idx: number, state?: boolean) => void;
  onMediaMetadataLoad?: (duration: number, mediaElRef: HTMLVideoElement | null) => void;
  getFrameTime?: (frame: number) => number;
  getFrameNumber?: (time: number) => number;
  updateFrame?: (time: number) => void;
  mediaFrameRate: number;
  updateToLastFrame?: () => void;
  isFramesLoading?: boolean;
  frameRate?: number;
  mediaVolume: number;
  onMediaVolumeChange: (volume: number) => void;
  /** Pauses the current video playback and seeks to the given frame */
  onSeekToFrame: (frame: number) => void;
  onVideoLoadStart?: (videoUrl: string, mediaFileReference: string) => void;
  /** The active HTMLVideoElement. Null until the video has mounted in the DOM. */
  mediaElement: HTMLVideoElement | null;
  /** Decoded frame rendering (canvas overlay for pixel-perfect frames) */
  decodedFrameRenderer?: {
    registerCanvas: (canvas: HTMLCanvasElement | null) => void;
    isDecoding: boolean;
  };
};
type PropsT = {
  children?: ReactNode;
  frameRate?: number;
  isVideoPlayer?: boolean;
  isAccurateFrameRenderingEnabled?: boolean;
};

//@ts-ignore
export const MediaStateContext = createContext<MediaStateContextT>({
  isPlaying: false,
  onPlaySpeedChange: _noop,
  onPlayStatusChange: _noop,
  updateFrameLoadStatus: _noop,
  onMediaMetadataLoad: _noop,
  mediaFrameLoadStatus: [],
  updateToLastFrame: _noop,
  isFramesLoading: false,
  mediaVolume: 1,
  onMediaVolumeChange: _noop,
  onSeekToFrame: _noop
});
const EPS = 0.0001; // to prevent the division floating point precision

const VIDEO_PLAYER_DEFAULT_FRAME = {
  imageUrl: '',
  annotations: []
};
export const MediaStateProvider = ({
  children,
  frameRate,
  isVideoPlayer: isVideoPlayerRendered,
  isAccurateFrameRenderingEnabled
}: PropsT) => {
  const dispatch = useSetAtom(dispatchAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const frames = useAtomValue(annotationFramesAtom);
  const fileType = useAtomValue(fileTypeAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const taskState = useTaskStateStore();
  const {
    videoFrameProcessor
  } = useAPIProvider();
  const {
    enqueue
  } = useSnackbar();
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isMainVideo = selectedVideoIndex === 0;
  const frameTable = useAtomValue(frameTableAtom);
  const accurateFrameCount = useAtomValue(accurateFrameCountAtom);
  const storedFrameCount = useAtomValue(frameCountAtom);
  const frameDetailsState = useAtomValue(frameDetailsStateAtom);
  const frameCount = accurateFrameCount ?? storedFrameCount;
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playSpeed, setPlaySpeed] = useState<number>(getDefaultPlaySpeed() ?? 1);
  const [mediaVolume, setMediaVolume] = useState<number>(1);
  const [mainMediaElement, setMainMediaElement] = useState<HTMLVideoElement | null>(null);
  // Added ref to avoid passing mainMediaElement as a dependency to useCallback
  // to make imperative mutations to video element.
  const mainMediaElementRef = useRef<HTMLVideoElement | null>(null);
  mainMediaElementRef.current = mainMediaElement;
  const [isFramesLoading, setIsFramesLoading] = useState<boolean>(!!isVideoPlayerRendered);
  const latestIsPlayingRef = useRef<boolean>(isPlaying);
  latestIsPlayingRef.current = isPlaying;

  // Track if pause is programmatic (from onSeekToFrame) to avoid double-decode
  const skipNextPauseDecodeRef = useRef<boolean>(false);

  // Track the last decoded frame to prevent redundant decodes (e.g., when PAUSE and ENDED both fire)
  const lastDecodedFrameRef = useRef<number | null>(null);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [mediaFrameLoadStatus, setMediaFrameLoadStatus] = useState<boolean[]>(getInitialFrameLoadStatus(frameCount, fileType));

  /**
   * Common logic to create/update frames with given frame count.
   * Also, handles merging with pre-annotated frames if they exist and updates the frame load status.
   */
  const updateVideoFrames = useCallback((newFrameCount: number) => {
    dispatch({
      type: EVENTS.SET_FRAME_COUNT,
      frameCount: newFrameCount
    });
    setIsFramesLoading(false);
    const existingFrameCount = frames.length;

    // If frame count hasn't changed, just update load status
    if (newFrameCount === existingFrameCount) {
      setMediaFrameLoadStatus(getInitialFrameLoadStatus(newFrameCount, fileType));
      return;
    }

    // Create new frames with annotation labels
    const newFrames = new Array(newFrameCount).fill(VIDEO_PLAYER_DEFAULT_FRAME);
    const annotationLabelMap = createAnnotationLabelMap(annotationLabels);
    const {
      initialFrames
    } = getInitialVideoFrames(newFrames, annotationLabelMap, taskState);
    if (existingFrameCount > 0) {
      const framesToMerge = frames.slice(0, newFrameCount);
      const updatedInitialFrames = _merge(initialFrames, framesToMerge);
      dispatch({
        type: EVENTS.CREATE_VIDEO_FRAMES,
        frames: updatedInitialFrames
      });
    } else {
      // No annotations, just use the new frames
      dispatch({
        type: EVENTS.CREATE_VIDEO_FRAMES,
        frames: initialFrames
      });
    }
    setMediaFrameLoadStatus(getInitialFrameLoadStatus(newFrameCount, fileType));
  }, [frames, annotationLabels, taskState, dispatch, fileType, setMediaFrameLoadStatus]);
  const onFrameDetailsExtracted = useCallback(({
    frameCount
  }: {
    frameCount: number;
  }) => {
    if (isAccurateFrameRenderingEnabled) updateVideoFrames(frameCount);
    setIsFramesLoading(false);
  }, [isAccurateFrameRenderingEnabled, updateVideoFrames]);
  const onFrameExtractionError = useCallback((error: string) => {
    // Fallback: use video duration and FPS to calculate frame count if extraction fails.
    const fallbackFrameCount = Math.round(mediaDuration * (frameRate ?? DEFAULT_FRAME_RATE));
    // No need to update frames if fallback frame count is 0 because initial frames are already created which doesn't show up if we update frames with 0 frames.
    if (fallbackFrameCount > 0) updateVideoFrames(fallbackFrameCount);
    if (!isAccurateFrameRenderingEnabled) return;
    console.error(`Failed to extract frames via mp4 demuxer. Please check if the ingested video is mp4 and fast-start.`);
    const clientMessage = getClientErrorMessage(error);
    if (clientMessage) {
      enqueue({
        message: clientMessage
      }, DURATION.medium);
    }
  }, [mediaDuration, frameRate, updateVideoFrames, isAccurateFrameRenderingEnabled, enqueue]);
  const setFrameDetails = useSetAtom(frameDetailsStateAtom);
  const shouldSkipExtraction = useCallback((mediaFileReference: string) => frameDetailsState.status !== FRAME_DETAILS_STATUS.IDLE && frameDetailsState.mediaFileReference === mediaFileReference, [frameDetailsState]);
  const {
    captureLatency: capturePerformanceLatency,
    incrementCount: incrementPerformanceCount
  } = usePerformanceMetrics();
  const measureLatency = useMeasureLatency();
  usePlaybackQualityMetrics(mainMediaElement);
  const {
    extractFrameDetails
  } = useFrameDetailsExtractor({
    videoFrameProcessor,
    onFrameDetailsExtracted,
    onFrameExtractionError,
    onStateChange: setFrameDetails,
    measureLatency,
    captureLatency: capturePerformanceLatency,
    incrementCount: incrementPerformanceCount
  });
  const wrappedExtractFrameDetails = useCallback((videoUrl: string, mediaFileReference: string) => {
    if (shouldSkipExtraction(mediaFileReference)) return;
    extractFrameDetails(videoUrl, mediaFileReference);
  }, [shouldSkipExtraction, extractFrameDetails]);
  const {
    decodeFrame
  } = useFrameDecoder({
    videoFrameProcessor,
    measureLatency,
    incrementCount: incrementPerformanceCount
  });
  const decodedFrameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDecodingFrame, setIsDecodingFrame] = useState<boolean>(false);
  const registerDecodedFrameCanvas = useCallback((canvas: HTMLCanvasElement | null) => {
    decodedFrameCanvasRef.current = canvas;
  }, []);
  const drawFrameToCanvas = useCallback((imageBitmap: ImageBitmap) => {
    const canvas = decodedFrameCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const drawStart = performance.now();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;
    ctx.drawImage(imageBitmap, 0, 0);
    const drawDuration = performance.now() - drawStart;

    // Only emit if > 1ms (sub-ms draws are noise)
    if (drawDuration > 1) {
      capturePerformanceLatency({
        name: PERFORMANCE_METRIC_EVENTS.FRAME_DECODE_CANVAS_DRAW_TIME,
        durationMs: drawDuration
      });
    }
  }, [capturePerformanceLatency]);
  const clearCanvas = useCallback(() => {
    const canvas = decodedFrameCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);
  const decodeAndRenderFrame = useCallback(async (frame: number): Promise<boolean> => {
    // Don't decode frames for context videos, only for main video (index 0)
    if (!isAccurateFrameRenderingEnabled || !videoFrameProcessor || !isMainVideo) {
      return false;
    }

    // Skip if we already decoded this exact frame (prevents duplicate decodes on PAUSE + ENDED events)
    if (lastDecodedFrameRef.current === frame) return true;
    lastDecodedFrameRef.current = frame;
    setIsDecodingFrame(true);
    try {
      const imageBitmap = await decodeFrame(frame);
      drawFrameToCanvas(imageBitmap);
      imageBitmap.close();
      return true;
    } catch (error) {
      clearCanvas();
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Failed to decode frame ${frame}. ${errorMessage}.`);
      return false;
    } finally {
      setIsDecodingFrame(false);
    }
  }, [isAccurateFrameRenderingEnabled, videoFrameProcessor, decodeFrame, drawFrameToCanvas, clearCanvas, setIsDecodingFrame, isMainVideo]);
  const onPlaySpeedChange = useCallback((updatedPlaySpeed: number) => {
    setPlaySpeed(updatedPlaySpeed);
  }, []);
  const onPlayStatusChange = useCallback((playbackState: boolean) => {
    setIsPlaying(playbackState);
  }, []);
  const onMediaVolumeChange = useCallback((volume: number) => {
    setMediaVolume(volume);
  }, []);
  const timeoutRef = useRef(-1);
  const updateFrameLoadStatus = useCallback((frameIndex: number, frameState = true) => {
    setMediaFrameLoadStatus(oldState => {
      if (oldState[frameIndex]) {
        return oldState;
      }
      const updatedState = [...oldState];
      updatedState[frameIndex] = frameState;
      return updatedState;
    });
  }, []);
  const onMediaMetadataLoad = useCallback((updatedMediaDuration: number, mediaElRef: HTMLVideoElement | null) => {
    if (frameRate === undefined) return;
    setMediaDuration(updatedMediaDuration);
    setMainMediaElement(mediaElRef);

    // If accurate frame rendering is enabled AND worker is ready AND extraction hasn't failed,
    // keep loading until demuxer completes. The demuxer will handle frame creation.
    const hasExtractionFailed = frameDetailsState.status === FRAME_DETAILS_STATUS.ERROR;
    if (isAccurateFrameRenderingEnabled && videoFrameProcessor && !hasExtractionFailed) {
      return;
    }

    // Fallback: Demuxer disabled, feature flag off, worker not ready, or extraction failed - use FPS-based frame creation.
    const updatedFrameCount = Math.round(updatedMediaDuration * frameRate);
    // No need to update frames if fallback frame count is 0 because initial frames are already created which doesn't show up if we update frames with 0 frames.
    if (updatedFrameCount > 0) updateVideoFrames(updatedFrameCount);
  }, [frameRate, videoFrameProcessor, isAccurateFrameRenderingEnabled, updateVideoFrames, setMediaDuration, setMainMediaElement, frameDetailsState.status]);
  const onFrameChange = useCallback((frameId: number) => {
    dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId
    });
  }, [dispatch]);
  useEffect(() => {
    if (isVideoPlayerRendered) return;
    if (isPlaying) {
      if (currentFrame < frameCount - 1) {
        //@ts-ignore
        timeoutRef.current = setTimeout(() => {
          if (!mediaFrameLoadStatus[currentFrame + 1] || !isPlaying) {
            return;
          }
          onFrameChange(currentFrame + 1);
        }, 1000 / (playSpeed * FPS));
      } else {
        onPlayStatusChange(false);
      }
    }
    return () => clearTimeout(timeoutRef.current);
  }, [isPlaying, currentFrame, frameCount, mediaFrameLoadStatus, onFrameChange, playSpeed, isVideoPlayerRendered, onPlayStatusChange, isLoading]);
  const getFrameTime = useCallback((frameNumber: number) => {
    // Use precise PTS timestamp from frame table if available and feature flag is enabled
    if (isAccurateFrameRenderingEnabled && frameTable?.[frameNumber]) {
      return Math.ceil(frameTable[frameNumber].timestamp * 1000000) / 1000000;
    }
    // Fallback to FPS-based calculation
    if (frameRate === undefined) return 0;
    if (frameNumber === 0) return 0;
    // ceiling to 6 decimal places to avoid JS truncation while setting the video duration
    return Math.ceil(frameNumber / frameRate * 1000000) / 1000000;
  }, [frameRate, frameTable, isAccurateFrameRenderingEnabled]);
  const getFrameNumber = useCallback((timeStamp: number, shouldUseExactFrame = false) => {
    if (_isNaN(timeStamp) || timeStamp === 0) {
      return 0;
    }
    if (shouldUseExactFrame && isAccurateFrameRenderingEnabled && frameTable) {
      return findClosestFrameByTimestamp(frameTable, timeStamp);
    }
    return Math.round((timeStamp + EPS) * frameCount / mediaDuration);
  }, [frameCount, mediaDuration, frameTable, isAccurateFrameRenderingEnabled]);
  const updateFrame = useCallback((timeStamp: number, shouldUseExactFrame = false) => {
    const frameId = getFrameNumber(timeStamp, shouldUseExactFrame);
    if (frameId < frameCount) {
      onFrameChange(frameId);
    }
  }, [getFrameNumber, onFrameChange, frameCount]);
  const updateFrameAndLoadStatus = useCallback((timeStamp: number, shouldUseExactFrame = false) => {
    const frameIdx = getFrameNumber(timeStamp, shouldUseExactFrame);
    updateFrameLoadStatus(frameIdx);
    updateFrame(timeStamp, shouldUseExactFrame);
  }, [getFrameNumber, updateFrame, updateFrameLoadStatus]);
  const updateToLastFrame = useCallback(() => {
    onFrameChange(frameCount - 1);
    updateFrameLoadStatus(frameCount - 1);
  }, [onFrameChange, frameCount, updateFrameLoadStatus]);
  const mediaFrameRate = useMemo(() => {
    return mediaDuration / frameCount;
  }, [frameCount, mediaDuration]);
  const onSeekToFrame = useCallback(async (frame: number) => {
    if (mainMediaElementRef.current && !mainMediaElementRef.current.paused) {
      // Video is playing - pause it and skip the auto-decode in handlePause
      skipNextPauseDecodeRef.current = true;
      mainMediaElementRef.current.pause();
      onPlayStatusChange(false);
    }
    await decodeAndRenderFrame(frame);
    onFrameChange(frame);
  }, [onFrameChange, onPlayStatusChange, decodeAndRenderFrame]);

  // Synchronize frame number with the video's playback position using
  // requestVideoFrameCallback. The standard 'timeupdate' event is not called
  // frequently enough for frame-accurate updates, so this effect uses
  // requestVideoFrameCallback to track playback time and update frame state on
  // each rendered frame.
  useEffect(() => {
    if (isLoading) return;
    if (!mainMediaElement) return;
    let cancelled = false;
    // rVFC id
    let rafId: number | null = null;
    let lastTime = -Infinity;
    const scheduleNextTick = () => {
      if (cancelled || rafId !== null) return;
      if (mainMediaElement.paused || mainMediaElement.ended || !isPlaying) return;
      rafId = mainMediaElement.requestVideoFrameCallback(tick);
    };
    const cancelNextTick = () => {
      const videoTime = mainMediaElement.currentTime || 0;
      updateFrameAndLoadStatus(videoTime, true);
      if (rafId === null) return;
      mainMediaElement.cancelVideoFrameCallback(rafId);
      rafId = null;
    };
    const tick = (_, metadata?: VideoFrameCallbackMetadata) => {
      // Clear rafId to allow scheduling next tick
      rafId = null;
      if (cancelled) return;
      const time = metadata?.mediaTime ?? mainMediaElement.currentTime ?? 0;
      // Ignore out-of-order frames, schedule next tick
      if (time + EPS >= lastTime) {
        lastTime = Math.max(lastTime, time);
        updateFrameAndLoadStatus(time);
      }
      scheduleNextTick();
    };
    const handlePlay = () => {
      lastTime = -Infinity;
      lastDecodedFrameRef.current = null;
      scheduleNextTick();
      clearCanvas();
    };
    const handlePause = () => {
      cancelNextTick();
      onPlayStatusChange(false);
      if (!skipNextPauseDecodeRef.current) {
        const videoTime = mainMediaElement.currentTime || 0;
        const frameIndex = getFrameNumber(videoTime, true);
        decodeAndRenderFrame(frameIndex);
      }

      // Always reset flag at the end, whether we decoded or skipped
      skipNextPauseDecodeRef.current = false;
    };
    mainMediaElement.addEventListener(MEDIA_EVENT_LISTENER.PLAY, handlePlay);
    mainMediaElement.addEventListener(MEDIA_EVENT_LISTENER.PAUSE, handlePause);
    mainMediaElement.addEventListener(MEDIA_EVENT_LISTENER.ENDED, handlePause);
    return () => {
      cancelled = true;
      cancelNextTick();
      mainMediaElement.removeEventListener(MEDIA_EVENT_LISTENER.PLAY, handlePlay);
      mainMediaElement.removeEventListener(MEDIA_EVENT_LISTENER.PAUSE, handlePause);
      mainMediaElement.removeEventListener(MEDIA_EVENT_LISTENER.ENDED, handlePause);
    };
  }, [isLoading, mainMediaElement, onPlayStatusChange, updateFrameAndLoadStatus, isPlaying, getFrameNumber, decodeAndRenderFrame, clearCanvas]);
  const contextValue = useMemo(() => ({
    isPlaying,
    playSpeed,
    mediaDuration,
    mediaFrameLoadStatus,
    onPlaySpeedChange,
    onPlayStatusChange,
    updateFrameLoadStatus,
    onMediaMetadataLoad,
    getFrameTime,
    getFrameNumber,
    updateFrame,
    mediaFrameRate,
    updateToLastFrame,
    isFramesLoading,
    frameRate,
    mediaVolume,
    onMediaVolumeChange,
    onSeekToFrame,
    mediaElement: mainMediaElement,
    onVideoLoadStart: isAccurateFrameRenderingEnabled && isMainVideo ? wrappedExtractFrameDetails : undefined,
    decodedFrameRenderer: {
      registerCanvas: registerDecodedFrameCanvas,
      isDecoding: isDecodingFrame
    }
  }), [isPlaying, playSpeed, mediaDuration, mediaFrameLoadStatus, onPlaySpeedChange, onPlayStatusChange, updateFrameLoadStatus, onMediaMetadataLoad, getFrameTime, getFrameNumber, updateFrame, mediaFrameRate, updateToLastFrame, isFramesLoading, frameRate, mediaVolume, mainMediaElement, onMediaVolumeChange, onSeekToFrame, isAccurateFrameRenderingEnabled, isMainVideo, wrappedExtractFrameDetails, registerDecodedFrameCanvas, isDecodingFrame]);
  return <MediaStateContext.Provider value={contextValue}>{children}</MediaStateContext.Provider>;
};